import React, { useMemo, useState } from 'react';
import { Autocomplete, TextField, Box, Typography } from '@mui/material';
import { Button } from '../../../../components/Button/Button';
import { AffinityCategory } from './affinity.types';

interface Props {
  categories: AffinityCategory[];
  // Effective category ids already linked (existing + pending adds − removes),
  // used to disable adding a duplicate.
  linkedCategoryIds: Set<number>;
  onAdd: (category: AffinityCategory) => void;
  disabled?: boolean;
}

export const CategoryLinkEditor: React.FC<Props> = ({
  categories,
  linkedCategoryIds,
  onAdd,
  disabled,
}) => {
  const [l1, setL1] = useState<AffinityCategory | null>(null);
  const [l2, setL2] = useState<AffinityCategory | null>(null);
  const [l3, setL3] = useState<AffinityCategory | null>(null);

  const l1Options = useMemo(
    () => categories.filter((c) => c.tier === 'L1').sort((a, b) => a.name.localeCompare(b.name)),
    [categories],
  );
  const l2Options = useMemo(
    () =>
      l1
        ? categories.filter((c) => c.parent_id === l1.id).sort((a, b) => a.name.localeCompare(b.name))
        : [],
    [categories, l1],
  );
  const l3Options = useMemo(
    () =>
      l2
        ? categories.filter((c) => c.parent_id === l2.id).sort((a, b) => a.name.localeCompare(b.name))
        : [],
    [categories, l2],
  );

  // The deepest level the admin actually picked is the category to link.
  const selected = l3 || l2 || l1;
  const alreadyLinked = selected ? linkedCategoryIds.has(selected.id) : false;

  const handleAdd = () => {
    if (!selected || alreadyLinked) return;
    onAdd(selected);
    setL1(null);
    setL2(null);
    setL3(null);
  };

  return (
    <Box className="flex flex-col gap-3">
      <Typography fontWeight={600} sx={{ fontSize: 14 }}>
        Add a category link
      </Typography>
      <Autocomplete
        size="small"
        value={l1}
        onChange={(_, v) => {
          setL1(v);
          setL2(null);
          setL3(null);
        }}
        options={l1Options}
        getOptionLabel={(o) => o.name || ''}
        isOptionEqualToValue={(o, v) => o.id === v.id}
        disabled={disabled}
        renderInput={(params) => <TextField {...params} label="L1 area" />}
      />
      <Autocomplete
        size="small"
        value={l2}
        onChange={(_, v) => {
          setL2(v);
          setL3(null);
        }}
        options={l2Options}
        getOptionLabel={(o) => o.name || ''}
        isOptionEqualToValue={(o, v) => o.id === v.id}
        disabled={disabled || !l1}
        renderInput={(params) => <TextField {...params} label="L2 family (optional)" />}
      />
      <Autocomplete
        size="small"
        value={l3}
        onChange={(_, v) => setL3(v)}
        options={l3Options}
        getOptionLabel={(o) => o.name || ''}
        isOptionEqualToValue={(o, v) => o.id === v.id}
        disabled={disabled || !l2}
        renderInput={(params) => <TextField {...params} label="L3 leaf (optional)" />}
      />
      {alreadyLinked && (
        <Typography color="var(--down)" sx={{ fontSize: 12 }}>
          {selected?.name} is already linked.
        </Typography>
      )}
      <Box>
        <Button onClick={handleAdd} disabled={disabled || !selected || alreadyLinked} className="!px-5">
          <Typography>{selected ? `Add ${selected.name}` : 'Add link'}</Typography>
        </Button>
      </Box>
    </Box>
  );
};
